import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Program {
  id: string
  name: string
  scope: string
  status: string
  visibility: string
  total_submissions: number
  valid_submissions: number
  total_paid: number
  researchers_enrolled: number
}

interface Submission {
  id: string
  program_id: string
  title: string
  severity: string
  status: string
  researcher: string
  bounty_amount: number
  cvss_score: number
}

interface Statistics {
  total_programs: number
  active_programs: number
  total_submissions: number
  valid_submissions: number
  total_paid_out: number
  avg_response_time_hours: number
  active_researchers: number
}

export default function BugBountyPage() {
  const [programs, setPrograms] = useState<Program[]>([])
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'programs' | 'submissions'>('programs')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [programsRes, submissionsRes, statsRes] = await Promise.all([
        fetch('/api/bugbounty/programs'),
        fetch('/api/bugbounty/submissions'),
        fetch('/api/bugbounty/statistics')
      ])

      if (programsRes.ok) setPrograms(await programsRes.json())
      if (submissionsRes.ok) setSubmissions(await submissionsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching bug bounty data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSeverityBadgeClass = (severity: string) => {
    const classes: Record<string, string> = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    }
    return classes[severity.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      closed: 'bg-gray-100 text-gray-800',
      new: 'bg-blue-100 text-blue-800',
      triaged: 'bg-purple-100 text-purple-800',
      accepted: 'bg-green-100 text-green-800',
      duplicate: 'bg-gray-100 text-gray-800',
      resolved: 'bg-green-100 text-green-800',
      paid: 'bg-emerald-100 text-emerald-800'
    }
    return classes[status.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Bug Bounty Program</h1>
          <p className="mt-2 text-gray-600">Manage bug bounty programs and researcher submissions</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Programs</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_programs}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{statistics.active_programs}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Submissions</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.total_submissions}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Valid</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.valid_submissions}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Paid</p>
              <p className="text-2xl font-bold text-emerald-600">${statistics.total_paid_out.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Response</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.avg_response_time_hours}h</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Researchers</p>
              <p className="text-2xl font-bold text-indigo-600">{statistics.active_researchers}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('programs')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'programs'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Programs ({programs.length})
            </button>
            <button
              onClick={() => setActiveTab('submissions')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'submissions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Submissions ({submissions.length})
            </button>
          </nav>
        </div>

        {/* Programs Grid */}
        {activeTab === 'programs' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {programs.map((program) => (
              <div key={program.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{program.name}</h3>
                    <p className="text-sm text-gray-500">{program.scope}</p>
                  </div>
                  <div className="flex gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(program.status)}`}>
                      {program.status}
                    </span>
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                      {program.visibility}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Submissions</p>
                    <p className="font-semibold">{program.total_submissions}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Valid</p>
                    <p className="font-semibold text-green-600">{program.valid_submissions}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Paid</p>
                    <p className="font-semibold text-emerald-600">${program.total_paid.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Researchers</p>
                    <p className="font-semibold">{program.researchers_enrolled}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Submissions Table */}
        {activeTab === 'submissions' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Researcher</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CVSS</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bounty</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {submissions.slice(0, 20).map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{submission.title}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {submission.researcher}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityBadgeClass(submission.severity)}`}>
                        {submission.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(submission.status)}`}>
                        {submission.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {submission.cvss_score.toFixed(1)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-emerald-600">
                      {submission.bounty_amount > 0 ? `$${submission.bounty_amount.toLocaleString()}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
